import { z } from "zod";
/**
 * Timestamp field in a response. Handlers emit whatever the source produced —
 * drizzle `timestamp` columns are JS Dates, raw SQL yields strings — and the
 * response serializer (zod parse) normalizes both to an ISO string. Inferred
 * type is `string`, which is what clients receive.
 */
export const dateStringSchema = z
    .union([z.date(), z.string()])
    .transform((v) => (v instanceof Date ? v.toISOString() : v));
/**
 * Error convention: every non-2xx response carries this body. Error-shaped
 * bodies must never ride on a 200 — empty results on a valid scope are 200
 * with null/empty data instead.
 */
export const errorResponseSchema = z.object({ error: z.string() });
/**
 * Numeric field computed by SQL (count/sum/avg/bigint arithmetic). postgres.js
 * returns these as strings (bigint/numeric safety) while other drivers (PGlite
 * in tests) return numbers — normalize both to number. Use plain z.number()
 * only for values that come from typed columns or JS arithmetic.
 * `.nullable()` composes correctly: null short-circuits before the transform.
 */
export const sqlNumberSchema = z
    .union([z.number(), z.string()])
    .transform((v) => Number(v));
// Shared enums used across multiple routes
export const txStatusSchema = z.enum([
    "dropped",
    "pending",
    "proposed",
    "checkpointed",
    "proven",
    "finalized",
]);
export const txExecutionResultSchema = z.enum([
    "success",
    "app_logic_reverted",
    "teardown_reverted",
    "both_reverted",
]);
export const pendingResetSchema = z.object({
    rollupAddress: z.string(),
    l1ChainId: z.number(),
    genesisTime: z.number(),
    aztecSlotDuration: z.number(),
    aztecEpochDuration: z.number(),
    // Optional, not nullable-required: a pending_reset row written before the indexer captured
    // this constant has no key for it at all, and demanding one would fail to parse a payload an
    // admin still needs to be able to apply.
    aztecProofSubmissionEpochs: z.number().nullish(),
    maxL2BlockGas: z.number(),
    detectedAt: z.string(),
});
export const networkSchema = z.object({
    id: z.string(),
    name: z.string(),
    nodeUrl: z.string(),
    chainId: z.number(),
    rollupAddress: z.string().nullable(),
    l1ChainId: z.number().nullable(),
    genesisTime: z.number().nullable(),
    aztecSlotDuration: z.number().nullable(),
    aztecEpochDuration: z.number().nullable(),
    aztecProofSubmissionEpochs: z.number().nullable(),
    maxL2BlockGas: z.number().nullable(),
    configUpdatedAt: dateStringSchema.nullable(),
    pendingReset: pendingResetSchema.nullable(),
    autoResetEnabled: z.boolean(),
    createdAt: dateStringSchema,
});
export const transactionSchema = z.object({
    id: z.number(),
    networkId: z.string(),
    txHash: z.string(),
    status: txStatusSchema,
    executionResult: txExecutionResultSchema.nullable(),
    error: z.string().nullable(),
    blockNumber: z.number().nullable(),
    txIndex: z.number().nullable(),
    actualFee: z.string().nullable(),
    numNoteHashes: z.number(),
    numNullifiers: z.number(),
    numL2ToL1Msgs: z.number(),
    numPrivateLogs: z.number(),
    numContractClassLogs: z.number(),
    gasLimitDa: z.number().nullable(),
    gasLimitL2: z.number().nullable(),
    maxFeePerDaGas: z.number().nullable(),
    maxFeePerL2Gas: z.number().nullable(),
    numSetupCalls: z.number(),
    numAppCalls: z.number(),
    hasTeardown: z.boolean(),
    totalPublicCalldataSize: z.number(),
    feePayer: z.string(),
    expirationTimestamp: z.number().nullable(),
    anchorBlockTimestamp: z.number().nullable(),
    numPublicDataWrites: z.number().nullable(),
    numPublicLogs: z.number().nullable(),
    privateLogTotalSize: z.number().nullable(),
    publicLogTotalSize: z.number().nullable(),
    hasPendingData: z.boolean(),
    // Deliberately NOT declared (the serializer strips them from responses):
    // publicCalls and l2ToL1MsgDetails JSONB blobs — the tx detail route serves
    // resolved versions at the top level; rawTxEffect — archival only.
    firstSeenAt: dateStringSchema,
    proposedAt: dateStringSchema.nullable(),
    createdAt: dateStringSchema,
});
// Common param schemas used by most routes
export const networkIdParamSchema = z.object({
    id: z.string(),
});
//# sourceMappingURL=common.js.map